<template>
  <div
    v-if="contentHubData"
    class="mx-auto px-24 pb-56 lg:max-w-[1120px] lg:px-0"
  >
    <RevBreadcrumbs
      v-if="breadcrumbs && contentHubData.displayBreadcrumb"
      ariaLabel="breadcrumb"
      :breads="breadcrumbs"
      class="pb-24 md:pb-32"
      data-test="content-hub-breadcrumb"
      @click-breadcrumb="trackBreadcrumbClick"
    />

    <TheMainContent
      class="flex flex-col"
      :class="{ 'mt-32': !contentHubData.displayBreadcrumb }"
    >
      <ContentHubHeroSection
        :align="contentHubData.align"
        :subtitle="contentHubData.introductionText"
        :title="contentHubData.title"
      />

      <ArticleCardsCollection
        v-if="contentHubData.featured"
        :articles="
          mapArticleCardsListToArticleCards(
            currentUrl,
            contentHubData.featured.props,
          )
        "
        class="mt-56"
        layout="carousel"
        :title="i18n(translations.featuredArticlesTitle)"
      />

      <div
        v-for="{ props, id } of contentHubData.categories"
        :key="id"
        class="mt-56"
      >
        <ArticleCardsCollection
          v-if="mapToArticles(props).length"
          :articles="mapArticleCardsListToArticleCards(currentUrl, props)"
          :cta="generateCTALink(props.ctaLink, props.title)"
          layout="carousel"
          :subtitle="props.subtitle"
          :title="props.title"
        />
      </div>

      <ArticleCardsCollection
        v-if="contentHubData.articles"
        :articles="
          mapArticlesToArticleCards(currentUrl, contentHubData.articles)
        "
        class="mt-56"
        layout="grid"
      />

      <CategoryTree
        v-if="contentHubData.categoryTree.props.categoryTree?.length"
        :categoryTree="contentHubData.categoryTree.props.categoryTree"
        :title="contentHubData.categoryTree.props.title"
        :tracking="trackingParams"
      />
    </TheMainContent>
  </div>
</template>

<script setup lang="ts">
import {
  useRequestURL,
  useRouteLocationWithLocale,
  useRouteParams,
} from '#imports'
import { computed } from 'vue'

import type { CTALink } from '@backmarket/http-api/src/api-specs-content/models/cta-link'
import ArticleCardsCollection from '@backmarket/nuxt-layer-cms/ArticleCardsCollection.vue'
import CategoryTree from '@backmarket/nuxt-layer-cms/CategoryTree.vue'
import {
  mapArticleCardsListToArticleCards,
  mapArticlesToArticleCards,
  mapToArticles,
} from '@backmarket/nuxt-layer-cms/utils/articleMapper'
import { transformHref } from '@backmarket/nuxt-layer-cms/utils/transformHref'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import { useCMSHead } from '~/scopes/cms/composables/useCMSHead'
import { useHandleFetchError } from '~/scopes/cms/composables/useHandleFetchError'

import { CMS } from '../../../cms/routes-names'
import breadcrumbTranslations from '../../../cms/shared-components/Breadcrumb/Breadcrumb.translations'
import { HOME } from '../../../home/route-names'
import ContentHubHeroSection from '../components/ContentHubHeroSection/ContentHubHeroSection.vue'
import { useFetchContentHub } from '../composables/useFetchContentHub'

import translations from './ContentHub.translations'

const currentUrl = useRequestURL()
const i18n = useI18n()
const { trackClick } = useTracking()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const routeParams = useRouteParams()

const { data: contentHubData, error } = await useFetchContentHub()

useHandleFetchError(error)
useCMSHead(contentHubData)

const trackingParams = { name: 'discover_our_articles', zone: CMS.ARTICLE_HUB }

const breadcrumbs = computed(() => {
  if (!contentHubData.value?.breadcrumb) return []

  const hasBreadcrumbs = contentHubData.value.breadcrumb.length > 0
  const remainingBreadcrumbs = contentHubData.value.breadcrumb.slice(1)

  return [
    {
      link: resolveLocalizedRoute({ name: HOME }),
      title: i18n(breadcrumbTranslations.home),
    },
    {
      link: resolveLocalizedRoute({ name: CMS.ARTICLE_ROOT_HUB }),
      title: hasBreadcrumbs
        ? contentHubData.value.breadcrumb[0].title
        : contentHubData.value.titleBreadcrumb,
    },
    ...remainingBreadcrumbs.map((b) => ({
      title: b.title,
      link: resolveLocalizedRoute({
        name: CMS.ARTICLE_HUB,
        params: { pageCategory: b.slug },
      }),
    })),
    ...(hasBreadcrumbs
      ? [
          {
            title: contentHubData.value.titleBreadcrumb,
            link: resolveLocalizedRoute({
              name: CMS.ARTICLE_HUB,
              params: { pageCategory: routeParams.pageCategory },
            }),
          },
        ]
      : []),
  ]
})

function generateCTALink(cta?: Maybe<CTALink>, ctaText?: Maybe<string>) {
  if (!cta?.link || !ctaText) return undefined

  return {
    link: transformHref({ href: cta.link.href, currentUrl }),
    label: ctaText,
  }
}

function trackBreadcrumbClick(breadcrumbValue: string) {
  trackClick({
    name: 'breadcrumb',
    zone: 'hubBuyingGuide',
    value: { breadcrumbValue },
  })
}
</script>
